import React, { useState, useEffect, useRef } from "react";
import * as _ from "lodash";

import Cell from "../Cell/Cell";
import {
  determineRows,
  determineCols,
  determineSubgridIdx,
  determineSubgrids,
  findValidVals,
} from "../../helper";
import sudokuChecker from "../../sudoku-checker";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import * as classes from "./Grid.module.css";

import solver, { solverAsync } from "../../sudoku-solver";
import puzzleGenerator from "../../sudoku-generator";

import useOutsideClick from "../../hooks/useOutsideClick";
import Timer from "../Timer/Timer";

import startGridVals from "../../fixtures/GridVals";

const CELL_HEIGHT = 50;
const CELL_WIDTH = CELL_HEIGHT;

const GRID_WIDTH = CELL_WIDTH * 9 + 2 * 2 * 9; // add the border pixels

export default function Grid() {
  // change when randomizing new puzzle
  const [origGridVals, setOrigGridVals] = useState([]);
  const [gridVals, setGridVals] = useState([]);
  const [rowArr, setRowArr] = useState([]);
  const [colArr, setColArr] = useState([]);
  const [subgridsArr, setSubgridsArr] = useState([]);
  const [win, setWin] = useState(false);

  const [activeCell, setActiveCell] = useState([]); // [i, j] of coordinate
  const [valsToRemove, setValsToRemove] = useState(30);
  const [delay, setDelay] = useState(250);
  const [validVals, setValidVals] = useState([]);
  // to force rerender
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const [highlightCell, setHighlightCell] = useState([]); // cell to highlight the row, col, and subgrid

  const [cellDim, setCellDim] = useState(50);
  const { height, width } = useWindowDimensions();

  const [timer, setTimer] = useState(0);
  const timerIntervalRef = useRef();
  const [paused, setPaused] = useState(false);
  const [showHint, setShowHint] = useState(true);

  const wrapperRef = useRef(null);
  useOutsideClick(wrapperRef, setHighlightCell);

  const controlsEnabledRef = useRef(); // when solving, then disable controls

  const updateGrid = (row, col, val) => {
    const newGrid = _.cloneDeep(gridVals);
    newGrid[row][col] = val;
    setGridVals(newGrid);
  };

  const updateRows = () => {
    const newRowsArr = determineRows(gridVals);
    setRowArr(newRowsArr);
  };

  const updateCols = () => {
    const newColsArr = determineCols(gridVals);
    setColArr(newColsArr);
  };

  const updateSubgrids = () => {
    const newSubgridsArr = determineSubgrids(gridVals);
    setSubgridsArr(newSubgridsArr);
  };

  const solveBtnHandler = () => {
    async function solverDelayed() {
      await solverAsync(
        newGridVals,
        (grid, activeCellIdx, validVals) => {
          setGridVals(grid);
          forceUpdate();
          setActiveCell(activeCellIdx);
          setValidVals(validVals);
        },
        delay
      );
      console.log("done solving");
      controlsEnabledRef.current = true;
      setWin(true);
      forceUpdate();
    }

    controlsEnabledRef.current = false;
    setHighlightCell([]);
    const newGridVals = _.cloneDeep(origGridVals);
    console.log("solving...");
    if (delay > 0) {
      solverDelayed();
    } else {
      window.numRecursiveCalls = 0;
      solver(newGridVals, {}, 9999999);
      console.log("done solving");
    }
    setGridVals(newGridVals);
    stopTimer();
  };

  const stopTimer = () => {
    console.log("stop timer");
    clearInterval(timerIntervalRef.current);
    setPaused(true);
    console.log(timerIntervalRef.current);
  };

  const startTimer = () => {
    console.log("start timer");
    timerIntervalRef.current = setInterval(() => {
      setTimer((timer) => timer + 1);
    }, 1000);
    setPaused(false);
  };

  const resetTimer = () => {
    setTimer(0);
    stopTimer();
    startTimer();
  };

  const newPuzzleBtnHandler = () => {
    setHighlightCell([]);
    setActiveCell([]);
    setValidVals([]);
    const newPuzzle = puzzleGenerator(valsToRemove);
    setOrigGridVals(newPuzzle);
    setGridVals(newPuzzle);
    resetTimer();
  };

  const changeDelayHandler = (e) => {
    setDelay(e.target.value);
  };

  const changeValsToRemoveHandler = (e) => {
    setValsToRemove(e.target.value);
  };

  const checkWin = () => {
    if (gridVals.length > 0) {
      const newRowsArr = determineRows(gridVals);
      const newColsArr = determineCols(gridVals);
      const newSubgridsArr = determineSubgrids(gridVals);

      const errors = sudokuChecker(newRowsArr, newColsArr, newSubgridsArr);
      let numErrors = 0;
      Object.keys(errors).forEach((e) => {
        numErrors += errors[[e]].reduce((a, b) => a + b, 0);
      });
      if (numErrors === 0) {
        setWin(true);
        stopTimer();
      } else {
        setWin(false);
      }
    }
  };

  useEffect(() => {
    if (width <= 600) {
      setCellDim(38);
    } else {
      setCellDim(50);
    }
  });

  useEffect(() => {
    startTimer();
    controlsEnabledRef.current = true;
    newPuzzleBtnHandler(valsToRemove);
    // setGridVals(startGridVals);
    // setOrigGridVals(startGridVals);
  }, []);

  useEffect(() => {
    const i = highlightCell[0];
    const j = highlightCell[1];
    const newValidVals = findValidVals(i, j, rowArr, colArr, subgridsArr);
    setValidVals(newValidVals);
  }, [highlightCell]);

  useEffect(() => {
    updateRows();
    updateCols();
    updateSubgrids();
    forceUpdate();
    checkWin();
  }, [gridVals, origGridVals]);

  return (
    <div className={classes.GameContainer}>
      <div
        className={classes.Grid}
        style={{ width: cellDim * 9 + 2 * 2 * 9 }}
        ref={wrapperRef}
      >
        {gridVals.map((row, i) => {
          return row.map((val, j) => {
            const subgridRow = (i + 1) % 3;
            const subgridCol = (j + 1) % 3;
            return (
              <Cell
                key={`${i}${j}`}
                val={val}
                row={i}
                col={j}
                height={cellDim}
                width={cellDim}
                origVal={origGridVals[i][j] != 0}
                updateGrid={updateGrid}
                drawBottomEdge={(subgridRow === 0) & (i !== 0)} // for subgrid
                drawRightEdge={(subgridCol === 0) & (j !== 0)} // for subgrid
                drawTopEdge={i === 0}
                drawLeftEdge={j === 0}
                activeCell={(i === activeCell[0]) & (j === activeCell[1])}
                setHighlightCell={setHighlightCell}
                highlightCell={
                  (highlightCell[0] === i) |
                  (highlightCell[1] === j) |
                  (determineSubgridIdx(highlightCell[0], highlightCell[1]) ===
                    determineSubgridIdx(i, j))
                }
              />
            );
          });
        })}
        {win ? (
          <div
            style={{ color: "green", fontSize: "100px", margin: "30px auto" }}
          >
            YOU WIN
          </div>
        ) : null}
      </div>
      <div className={classes.InputContainer}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className={classes.PlayPauseIcon}
            onClick={!paused ? stopTimer : startTimer}
          >
            {!paused ? (
              <i className="fa fa-pause"></i>
            ) : (
              <i className="fa fa-play"></i>
            )}
          </div>
          <Timer
            timer={timer}
            setTimer={setTimer}
            timerIntervalRef={timerIntervalRef}
          />
        </div>
        <div
          className={
            controlsEnabledRef.current & !win
              ? classes.Button
              : [classes.Button, classes.Disabled].join(" ")
          }
          onClick={() => {
            if (controlsEnabledRef.current & !win) solveBtnHandler();
          }}
        >
          Solve
        </div>
        <input
          type="range"
          min="0"
          max="1000"
          value={delay}
          onChange={changeDelayHandler}
          disabled={!controlsEnabledRef.current}
        />
        {delay} ms / step
        <div
          className={
            controlsEnabledRef.current
              ? classes.Button
              : [classes.Button, classes.Disabled].join(" ")
          }
          style={{ marginTop: 40 }}
          onClick={() => {
            if (controlsEnabledRef.current) newPuzzleBtnHandler();
          }}
        >
          New Puzzle
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          Number of cells to remove
          <p style={{ fontSize: "0.8em" }}>(Removing more takes longer)</p>
          <input
            type="range"
            min="1"
            max={81 - 17}
            value={valsToRemove}
            onChange={changeValsToRemoveHandler}
            disabled={!controlsEnabledRef.current}
          />
          {valsToRemove} Cells
        </div>
        <hr />
        <div>
          <input
            type="checkbox"
            checked={showHint}
            onClick={() => {
              setShowHint((showHint) => !showHint);
            }}
            name="hint"
          ></input>
          <label for="hint">Hint (valid values)</label>
          <h1 className={classes.Hint}>
            {showHint ? (
              (validVals.length > 0) |
              ((highlightCell.length > 0) | (activeCell.length > 0)) ? (
                validVals.join(", ")
              ) : null
            ) : (
              <span>&nbsp;&nbsp;</span>
            )}
          </h1>
        </div>
      </div>
    </div>
  );
}
